// @ts-check
// Compilador grafo de arcos -> IR estructurada (TSK-0193, materializa DEC-069
// punto 4 -- docs/generated/decisions.md#DEC-069).
//
// Contexto: `arrow-connection-graph.js` (TSK-0191) modela el grafo EXPLÍCITO
// de arcos que el alumno dibuja; `arrow-graph-derivation.js` (TSK-0192) es el
// adaptador INVERSO (IR ya existente -> grafo). Este módulo cierra el ciclo:
// `compilarGrafoAIr(nodos, grafo)` recupera la IR estructurada
// (`Secuencia`/`Condicional`/`Bucle*`) recorriendo el grafo de arcos desde
// `'inicio'`, para que "Ejecutar"/"Corrección"/persistencia (TSK-0194/0195)
// puedan compilar lo que el alumno dibujó al formato que ya consume
// `ir-serialization.js`/el backend, SIN cambiar ese contrato. Puro: sin DOM
// ni `fetch`. Reutiliza únicamente la API pública de `arrow-connection-graph.js`
// (`PUERTO`, `arcosDesde`) y de `editor-model.js` (`TIPO_NODO` + las
// factorías `crear*`) -- ninguno de los dos se edita aquí (rutas prohibidas
// de la ficha de esta tarea, igual que en TSK-0191/TSK-0192).
//
// ---------------------------------------------------------------------------
// AUTORIDAD ESTRUCTURAL (DEC-070, docs/generated/decisions.md#DEC-070) --
// LEER ANTES DE TOCAR ESTE MÓDULO O `validarGrafoArcos`:
// ---------------------------------------------------------------------------
// `validarGrafoArcos` (TSK-0191) exige, LOCALMENTE y sin excepción, como
// mucho 1 arco entrante a un bloque normal y EXACTAMENTE 1 a `'fin'`. Pero un
// `Condicional` con continuación real tras él SIEMPRE produce, por topología
// de diamante, un nodo de convergencia con 2 arcos entrantes (uno por rama) a
// ese mismo bloque/`'fin'` -- disparando `MULTIPLES_ENTRADAS` en
// `validarGrafoArcos` de forma sistemática para el caso más común (un
// `Condicional` con continuación). DEC-070 resuelve esta contradicción
// asignando la AUTORIDAD estructural a `compilarGrafoAIr`: el grafo resuelve
// a un programa válido SI Y SOLO SI esta función devuelve `programa != null`
// (con `errores` concretos en caso contrario). Este compilador ACEPTA y
// RECONSTRUYE deliberadamente el nodo de convergencia con 2 entradas -- es
// exactamente cómo reconoce el post-dominador de un `Condicional` -- aunque
// `validarGrafoArcos` lo marque como falso positivo `MULTIPLES_ENTRADAS`.
// `validarGrafoArcos` NO se llama desde este módulo ni debe usarse como
// puerta de "Ejecutar"/"Corrección" en las tareas que consuman este
// compilador (TSK-0194/TSK-0195): sigue siendo una PISTA LOCAL de edición en
// vivo (DEC-069 punto 3), no la fuente de verdad de validez estructural. La
// corrección de fondo de `validarGrafoArcos` queda diferida (DEC-070 punto
// 3), fuera de las rutas permitidas de esta ficha.
//
// ---------------------------------------------------------------------------
// Algoritmo
// ---------------------------------------------------------------------------
// `compilarGrafoAIr` recorre el grafo como una máquina de estados sobre
// "posiciones" (`{ nodoId, puerto }` -- el siguiente arco a seguir -- o
// `{ directo: nodoId }` -- un destino ya conocido, sin arco que buscar, ver
// más abajo) hasta alcanzar un nodo de PARADA (`pararEn`):
//
// - La `Secuencia` raíz se compila con `pararEn = 'fin'`, empezando en
//   `inicio.SALIDA`.
// - Un bloque normal (Entrada/Salida/Asignacion/Operacion/Llamada/Retorno) se
//   reutiliza TAL CUAL (el propio `NodoModelo` ya tiene todos sus campos
//   completos -- `variable`/`expresion`/`condicion`/etc. -- no dependen del
//   grafo) y la cadena continúa desde su puerto `SALIDA`.
// - `Condicional`: se localiza el POST-DOMINADOR (el nodo de convergencia
//   común de ambas ramas) mediante `resolverConvergencia` (ver más abajo) y
//   se compilan `ramaSi`/`ramaSino` con `pararEn = <convergencia>` (sin
//   consumir ese nodo: pertenece a la cadena EXTERIOR, que continúa desde él
//   como un destino "directo", sin arco propio -- un `Condicional` no tiene
//   puerto `SALIDA` ordinario). Si una rama no dibuja ningún arco (llega
//   directa a la convergencia), esa rama compila a `[]`; `ramaSi` se
//   construye siempre como `Secuencia` (aunque esté vacía, igual que
//   `crearCondicional` exige `ramaSi` no-opcional); `ramaSino` se pasa como
//   `null` cuando su cadena compilada queda vacía (coherente con
//   `derivarGrafoDeIr`, que solo dibuja un arco directo `FALSO ->
//   convergencia` cuando `rama_sino` es `null`/ausente -- indistinguible en
//   el grafo de una `Secuencia` vacía explícita, así que se normaliza siempre
//   a `null` en ese caso).
// - `Bucle*` (BucleMientras/BucleFinPara/RepetirHasta): el cuerpo se compila
//   desde el puerto `CUERPO` con `pararEn = null` (retorno implícito, DEC-069
//   punto 1: el cuerpo termina en cuanto se queda sin arco de salida, NUNCA
//   dibujando un arco de vuelta al propio bucle ni a `'fin'`); la cadena
//   EXTERIOR continúa desde el puerto `SALIDA` del bucle (como un bloque
//   normal).
// - Post-dominador (`buscarConvergencia`): se calcula la alcanzabilidad
//   genérica (BFS ignorando a qué puerto pertenece cada arco: sigue TODOS
//   los arcos salientes de cada nodo visitado) desde el destino de la rama
//   `VERDADERO` y, por separado, desde el de `FALSO`. El post-dominador es el
//   nodo alcanzable desde AMBAS con la suma de distancias mínima (el más
//   cercano al `Condicional`). Si no existe ningún nodo común alcanzable
//   desde ambas ramas (ni siquiera `'fin'`), el resultado depende del
//   contexto: si la cadena que contiene al `Condicional` espera un punto de
//   continuación real (`pararEn` de esa cadena distinto de `null` -- 'fin' o
//   un hermano siguiente), las ramas NO convergen de forma resoluble --
//   error `CONDICIONAL_SIN_CONVERGENCIA`. Pero si esa cadena exterior YA es
//   de retorno implícito (`pararEn === null`, cuerpo de un `Bucle*`, el
//   `Condicional` siendo su última sentencia), la ausencia de convergencia
//   es VÁLIDA: cada rama termina, independientemente, en su propio retorno
//   implícito (exactamente lo que dibuja `derivarGrafoDeIr` en ese caso) --
//   `compilarCondicional` compila entonces ambas ramas con `pararEn = null` y
//   devuelve `siguiente: null` para que la cadena exterior también termine
//   ahí, sin buscar más arcos (ver `compilarNodo`/`compilarCadena`).
// - Ciclo NO modelado por un `Bucle*`: cualquier arco que vuelva a un nodo ya
//   consumido en ESTA compilación (`this.consumidos`, un `Set` que nunca se
//   vacía) que no sea, en ese momento, el destino esperado (`pararEn`) es un
//   error `CICLO`. Como el punto de convergencia legítimo de un `Condicional`
//   se comprueba SIEMPRE antes que `consumidos` (comparación directa con
//   `pararEn`), esto detecta exactamente los arcos de retorno ilegítimos sin
//   necesitar una pila de ámbito por rama: la única forma válida de volver a
//   visitar un nodo ya recorrido es que sea el post-dominador esperado.
// - Bifurcación ilegal fuera de `Condicional`/`Bucle*`: cualquier puerto con
//   más de 1 arco saliente donde solo se espera 1 (`SALIDA` de un bloque
//   normal, `SALIDA` de `inicio`, `SALIDA`/`CUERPO` de un `Bucle*`) es un
//   error `BIFURCACION_ILEGAL`.
// - Grafo desconectado de `'inicio'`/`'fin'`: si `'inicio'`/`'fin'` no
//   figuran en `nodos`, error inmediato (`INICIO_AUSENTE`/`FIN_AUSENTE`). Si
//   la cadena principal no logra llegar a `'fin'` (se queda sin arco antes),
//   error `CADENA_INCOMPLETA`. Al terminar con éxito la compilación de la
//   cadena principal, se comprueba que TODOS los nodos de `nodos` (salvo
//   `'inicio'`/`'fin'`/`Comentario`, ver más abajo) fueron consumidos; los
//   que no lo fueron son bloques inalcanzables -- error `NODO_INALCANZABLE`
//   por cada uno.
//
// ---------------------------------------------------------------------------
// Ambigüedades resueltas (repórtense también en la respuesta final del
// agente ejecutor):
// ---------------------------------------------------------------------------
// 1. `Comentario` (DEC-069 punto 1: sin puertos, no participa del flujo) NO
//    puede ser destino de ningún arco -- `arrow-graph-derivation.js` ya lo
//    excluye de la cadena de conexión al derivar. En consecuencia
//    `compilarGrafoAIr` NUNCA lo encuentra al recorrer el grafo y no puede
//    recuperar su posición original en la `Secuencia`: se excluye
//    deliberadamente de la comprobación de "nodo inalcanzable" (no sería un
//    error real, es una limitación estructural conocida y simétrica a la de
//    `derivarGrafoDeIr`) y NO aparece en el `programa` compilado. Los
//    fixtures de round-trip de este módulo no incluyen `Comentario` por este
//    motivo (documentado, no oculto).
// 2. `programa.declaraciones`/`programa.funciones` quedan siempre vacíos
//    (`[]`) en el `Programa` que produce este módulo: el grafo de arcos solo
//    modela el flujo de UN cuerpo (mismo alcance que `derivarGrafoDeIr`, ver
//    su cabecera, ambigüedad 1). Reconstruir declaraciones/funciones es
//    responsabilidad de quien orqueste la llamada (TSK-0194/0195), fuera del
//    alcance de esta ficha.
// 3. Los `id` de los nodos del `programa` compilado son NUEVOS (generados por
//    las factorías `crear*` de `editor-model.js`) para `Secuencia`/
//    `Condicional`/`Bucle*`; los bloques normales (Entrada/Salida/
//    Asignacion/Operacion/Llamada/Retorno) SÍ conservan su `id`/campos
//    originales tal cual figuran en `nodos` (se reutiliza el propio
//    `NodoModelo`, no se reconstruye con `crear*`: sus campos no dependen del
//    grafo). El criterio de aceptación de la ficha ("los ids pueden diferir,
//    compara por estructura/campos") se cumple igualmente comparando por
//    estructura, no por identidad de `id`.
// 4. Revisitar un nodo (posible "bifurcación ilegal" O "ciclo") se distingue
//    así: más de 1 arco saliente del MISMO puerto donde se espera como mucho
//    1 -> `BIFURCACION_ILEGAL` (detectado en el origen, antes de seguir el
//    arco); un arco que aterriza en un nodo ya consumido que no es el
//    `pararEn` esperado -> `CICLO` (detectado en el destino). Ambos casos
//    quedan cubiertos con tests dedicados.

import { PUERTO, arcosDesde } from './arrow-connection-graph.js';
import {
  TIPO_NODO,
  crearPrograma,
  crearSecuencia,
  crearCondicional,
  crearBucleMientras,
  crearBucleFinPara,
  crearRepetirHasta,
} from './editor-model.js';

/**
 * @typedef {import('./editor-model.js').NodoModelo} NodoModelo
 * @typedef {import('./arrow-connection-graph.js').GrafoArcos} GrafoArcos
 * @typedef {import('./arrow-connection-graph.js').ValorPuerto} ValorPuerto
 * @typedef {import('./arrow-connection-graph.js').NodoOClave} NodoOClave
 */

/**
 * Un problema concreto detectado por `compilarGrafoAIr`: `tipo` es un código
 * estable (para que TSK-0194 pueda mapearlo a un mensaje/estilo de UI),
 * `mensaje` ya es legible para mostrar directamente.
 * @typedef {{ tipo: string, nodoId?: string, arcoId?: string, mensaje: string }} ErrorCompilacion
 */

/** @typedef {{ programa: NodoModelo | null, errores: ErrorCompilacion[] }} ResultadoCompilacion */

/**
 * Posición desde la que continuar el recorrido de una cadena: o bien "busca
 * el arco único que sale de (nodoId, puerto)" (`modo: 'arco'`), o bien "el
 * siguiente nodo YA es conocido, no hay arco que buscar" (`modo: 'directo'`
 * -- el caso de un `Condicional`, que no tiene puerto `SALIDA` ordinario: su
 * post-dominador ya se resolvió antes de continuar la cadena exterior).
 * @typedef {{ modo: 'arco', nodoId: string, puerto: ValorPuerto } | { modo: 'directo', nodoId: string }} Posicion
 */

const CLAVE_INICIO = 'inicio';
const CLAVE_FIN = 'fin';

/** Nodos de bucle: mismo conjunto que `NODOS_BUCLE` de
 * `arrow-connection-graph.js`/`arrow-graph-derivation.js`, redefinido aquí de
 * forma autocontenida (ninguno de esos módulos lo exporta). */
const NODOS_BUCLE = /** @type {Set<string>} */ (
  new Set([TIPO_NODO.BUCLE_MIENTRAS, TIPO_NODO.BUCLE_FIN_PARA, TIPO_NODO.REPETIR_HASTA])
);

/** Bloques normales (1 `ENTRADA` + 1 `SALIDA`, DEC-069 punto 1) que se
 * reutilizan tal cual (ver ambigüedad 3 de la cabecera). */
const TIPOS_BLOQUE_NORMAL = /** @type {Set<string>} */ (
  new Set([
    TIPO_NODO.ENTRADA,
    TIPO_NODO.SALIDA,
    TIPO_NODO.ASIGNACION,
    TIPO_NODO.OPERACION,
    TIPO_NODO.LLAMADA,
    TIPO_NODO.RETORNO,
  ])
);

/**
 * @param {NodoOClave} nodoOClave
 * @returns {string}
 */
function idDe(nodoOClave) {
  return typeof nodoOClave === 'string' ? nodoOClave : nodoOClave.id;
}

/**
 * Recorre y compila el grafo de arcos, manteniendo el estado mutable interno
 * (`errores`/`consumidos`) de UNA compilación (`compilarGrafoAIr` construye
 * una instancia nueva en cada llamada). No se expone: es un detalle de
 * implementación.
 */
class ContextoCompilacion {
  /**
   * @param {Map<string, NodoOClave>} nodosPorId
   * @param {GrafoArcos} grafo
   */
  constructor(nodosPorId, grafo) {
    this.nodosPorId = nodosPorId;
    this.grafo = grafo;
    /** @type {ErrorCompilacion[]} */
    this.errores = [];
    /** Ids ya consumidos (convertidos en sentencia) en TODA la compilación;
     * nunca se vacía (ver ambigüedad 4/algoritmo de la cabecera): permite
     * detectar tanto nodos inalcanzables (los que quedan fuera) como
     * revisitas ilegítimas (`CICLO`) sin necesitar una pila por rama. */
    this.consumidos = /** @type {Set<string>} */ (new Set());
  }

  /**
   * @param {ErrorCompilacion} error
   */
  registrarError(error) {
    this.errores.push(error);
  }

  /**
   * Arco único saliente de `(nodoId, puerto)`. Devuelve `{ error: true }` si
   * hay más de uno (bifurcación ilegal, ya registrada); `{ error: false,
   * arco: null }` si no hay ninguno; `{ error: false, arco }` en otro caso.
   * @param {string} nodoId
   * @param {ValorPuerto} puerto
   * @returns {{ error: true } | { error: false, arco: import('./arrow-connection-graph.js').Arco | null }}
   */
  arcoUnico(nodoId, puerto) {
    const candidatos = arcosDesde(this.grafo, nodoId, puerto);
    if (candidatos.length > 1) {
      this.registrarError({
        tipo: 'BIFURCACION_ILEGAL',
        nodoId,
        mensaje:
          `el nodo '${nodoId}' tiene ${candidatos.length} arcos salientes desde el puerto ` +
          `'${puerto}'; fuera de un Condicional, un bloque admite como máximo 1 arco de salida`,
      });
      return { error: true };
    }
    return { error: false, arco: candidatos[0] ?? null };
  }

  /**
   * Alcanzabilidad genérica (BFS por TODOS los arcos salientes, sin filtrar
   * por puerto) desde `nodoInicialId`. Se usa únicamente para localizar el
   * post-dominador de un `Condicional` (ver cabecera del módulo): incluye
   * `nodoInicialId` con distancia 0.
   * @param {string} nodoInicialId
   * @returns {Map<string, number>}
   */
  alcanzables(nodoInicialId) {
    /** @type {Map<string, number>} */
    const distancias = new Map([[nodoInicialId, 0]]);
    /** @type {string[]} */
    const cola = [nodoInicialId];
    let cabeza = 0;
    while (cabeza < cola.length) {
      const actual = cola[cabeza];
      cabeza += 1;
      const distanciaActual = /** @type {number} */ (distancias.get(actual));
      for (const arco of arcosDesde(this.grafo, actual)) {
        const destino = arco.hastaNodoId;
        if (!distancias.has(destino)) {
          distancias.set(destino, distanciaActual + 1);
          cola.push(destino);
        }
      }
    }
    return distancias;
  }

  /**
   * Post-dominador de un `Condicional`: el nodo alcanzable desde
   * `verdaderoDestino` Y desde `falsoDestino` con la suma de distancias
   * mínima, o `null` si ninguno es común (sin registrar error: quien llama
   * decide si eso es o no un problema, ver `compilarCondicional`).
   * @param {string} verdaderoDestino
   * @param {string} falsoDestino
   * @returns {string | null}
   */
  buscarConvergencia(verdaderoDestino, falsoDestino) {
    const distV = this.alcanzables(verdaderoDestino);
    const distF = this.alcanzables(falsoDestino);
    let mejor = /** @type {string | null} */ (null);
    let mejorPeso = Infinity;
    for (const [id, distancia] of distV) {
      const distanciaFalso = distF.get(id);
      if (distanciaFalso === undefined) continue;
      const peso = distancia + distanciaFalso;
      if (peso < mejorPeso) {
        mejorPeso = peso;
        mejor = id;
      }
    }
    return mejor;
  }

  /**
   * Compila la cadena de sentencias que empieza en `origen` y termina justo
   * ANTES de `pararEn` (el nodo de convergencia/parada, sin consumirlo:
   * pertenece a la cadena exterior) o, si `pararEn` es `null`, en cuanto la
   * cadena se queda sin arco de salida (retorno implícito de un cuerpo de
   * `Bucle*`).
   * @param {Posicion} origen
   * @param {string | null} pararEn
   * @returns {NodoModelo[] | null}
   */
  compilarCadena(origen, pararEn) {
    /** @type {NodoModelo[]} */
    const sentencias = [];
    /** @type {Posicion} */
    let siguiente = origen;

    for (;;) {
      /** @type {string} */
      let destinoId;
      /** @type {string | undefined} */
      let arcoId;

      if (siguiente.modo === 'arco') {
        const resultado = this.arcoUnico(siguiente.nodoId, siguiente.puerto);
        if (resultado.error) return null;
        if (resultado.arco === null) {
          if (pararEn === null) return sentencias; // retorno implícito: fin de cuerpo de bucle
          this.registrarError({
            tipo: 'CADENA_INCOMPLETA',
            nodoId: siguiente.nodoId,
            mensaje:
              `la cadena desde '${siguiente.nodoId}' (puerto '${siguiente.puerto}') no llega a ` +
              `'${pararEn}': no tiene arco de salida`,
          });
          return null;
        }
        destinoId = resultado.arco.hastaNodoId;
        arcoId = resultado.arco.id;
      } else {
        destinoId = siguiente.nodoId;
      }

      if (pararEn !== null && destinoId === pararEn) return sentencias; // convergencia alcanzada

      const destinoEntrada = this.nodosPorId.get(destinoId);
      if (destinoEntrada === undefined) {
        this.registrarError({
          tipo: 'NODO_DESCONOCIDO',
          nodoId: destinoId,
          ...(arcoId === undefined ? {} : { arcoId }),
          mensaje: `${arcoId === undefined ? 'la cadena' : `el arco '${arcoId}'`} llega a un nodo desconocido '${destinoId}'`,
        });
        return null;
      }
      if (destinoEntrada === CLAVE_INICIO) {
        this.registrarError({
          tipo: 'ARCO_A_INICIO',
          nodoId: CLAVE_INICIO,
          ...(arcoId === undefined ? {} : { arcoId }),
          mensaje: "'inicio' no admite arcos entrantes" + (arcoId === undefined ? '' : ` (arco '${arcoId}')`),
        });
        return null;
      }
      if (destinoEntrada === CLAVE_FIN) {
        this.registrarError({
          tipo: 'CONVERGENCIA_NO_RESUELTA',
          nodoId: CLAVE_FIN,
          ...(arcoId === undefined ? {} : { arcoId }),
          mensaje:
            pararEn === null
              ? "el cuerpo de un bucle conecta directamente a 'fin'; el cuerpo debe terminar por " +
                "retorno implícito (sin arco de salida), no conectando a 'fin'"
              : `la cadena llega a 'fin' sin pasar por el punto de convergencia esperado ('${pararEn}')`,
        });
        return null;
      }

      if (this.consumidos.has(destinoId)) {
        this.registrarError({
          tipo: 'CICLO',
          nodoId: destinoId,
          ...(arcoId === undefined ? {} : { arcoId }),
          mensaje:
            `el nodo '${destinoId}' ya fue procesado y no es el punto de convergencia esperado; ` +
            'el grafo dibujado debe ser acíclico -- usa un bloque Bucle* (puerto CUERPO) para ' +
            'expresar la repetición en vez de un arco de retorno',
        });
        return null;
      }

      this.consumidos.add(destinoId);
      const resultadoNodo = this.compilarNodo(/** @type {NodoModelo} */ (destinoEntrada), pararEn);
      if (resultadoNodo === null) return null;
      sentencias.push(resultadoNodo.sentencia);
      if (resultadoNodo.siguiente === null) return sentencias; // retorno implícito transitivo (ver compilarCondicional)
      siguiente = resultadoNodo.siguiente;
    }
  }

  /**
   * Compila un único nodo ya consumido, devolviendo su sentencia compilada y
   * la `Posicion` desde la que la cadena exterior debe continuar (`null` si
   * esa cadena exterior ya termina aquí en retorno implícito, ver
   * `compilarCondicional`).
   * @param {NodoModelo} nodo
   * @param {string | null} pararEn `pararEn` de la cadena que está
   *   consumiendo este nodo (necesario para que un `Condicional` sin
   *   convergencia real sepa si eso es un error o un retorno implícito
   *   transitivo válido).
   * @returns {{ sentencia: NodoModelo, siguiente: Posicion | null } | null}
   */
  compilarNodo(nodo, pararEn) {
    if (nodo.nodo === TIPO_NODO.CONDICIONAL) return this.compilarCondicional(nodo, pararEn);
    if (NODOS_BUCLE.has(nodo.nodo)) return this.compilarBucle(nodo);
    if (TIPOS_BLOQUE_NORMAL.has(nodo.nodo)) {
      // Ambigüedad 3 (cabecera): se reutiliza el propio NodoModelo tal cual
      // -- sus campos (`variable`/`expresion`/etc.) no dependen del grafo.
      return { sentencia: nodo, siguiente: { modo: 'arco', nodoId: nodo.id, puerto: PUERTO.SALIDA } };
    }
    // Comentario (u otro tipo no soportado como paso de flujo) no debería
    // llegar aquí: no tiene puertos (DEC-069 punto 1), nunca es destino de un
    // arco. Si aparece, el grafo es corrupto (nodo inventado con id de un
    // tipo no reconocido).
    this.registrarError({
      tipo: 'TIPO_NODO_NO_SOPORTADO',
      nodoId: nodo.id,
      mensaje: `el nodo '${nodo.id}' (tipo '${nodo.nodo}') no puede aparecer como paso de flujo del grafo`,
    });
    return null;
  }

  /**
   * @param {NodoModelo} nodo
   * @param {string | null} pararEnExterior `pararEn` de la cadena que
   *   contiene a este `Condicional` (ver `compilarNodo`).
   * @returns {{ sentencia: NodoModelo, siguiente: Posicion | null } | null}
   */
  compilarCondicional(nodo, pararEnExterior) {
    const verdaderoArcos = arcosDesde(this.grafo, nodo.id, PUERTO.VERDADERO);
    const falsoArcos = arcosDesde(this.grafo, nodo.id, PUERTO.FALSO);
    let huboError = false;

    if (verdaderoArcos.length === 0) {
      this.registrarError({
        tipo: 'CONDICIONAL_RAMA_FALTANTE',
        nodoId: nodo.id,
        mensaje: `falta el arco de la rama verdadero del condicional '${nodo.id}'`,
      });
      huboError = true;
    } else if (verdaderoArcos.length > 1) {
      this.registrarError({
        tipo: 'CONDICIONAL_RAMA_DUPLICADA',
        nodoId: nodo.id,
        mensaje: `el condicional '${nodo.id}' tiene ${verdaderoArcos.length} arcos en la rama verdadero; debe tener exactamente 1`,
      });
      huboError = true;
    }

    if (falsoArcos.length === 0) {
      this.registrarError({
        tipo: 'CONDICIONAL_RAMA_FALTANTE',
        nodoId: nodo.id,
        mensaje: `falta el arco de la rama falso del condicional '${nodo.id}'`,
      });
      huboError = true;
    } else if (falsoArcos.length > 1) {
      this.registrarError({
        tipo: 'CONDICIONAL_RAMA_DUPLICADA',
        nodoId: nodo.id,
        mensaje: `el condicional '${nodo.id}' tiene ${falsoArcos.length} arcos en la rama falso; debe tener exactamente 1`,
      });
      huboError = true;
    }

    if (huboError) return null;

    const verdaderoDestino = verdaderoArcos[0].hastaNodoId;
    const falsoDestino = falsoArcos[0].hastaNodoId;

    const convergencia = this.buscarConvergencia(verdaderoDestino, falsoDestino);
    // BUG REAL corregido aquí (detectado por el fixture de round-trip
    // "Condicional anidado dentro del cuerpo de un Bucle" del spec, sin
    // sentencias tras el Condicional): un `Condicional` que es la ÚLTIMA
    // sentencia de una cadena que YA es de retorno implícito (`pararEnExterior
    // === null`, cuerpo de un `Bucle*`) puede legítimamente NO converger en
    // ningún nodo real -- cada rama termina, independientemente, en su propio
    // retorno implícito (exactamente lo que dibuja `derivarGrafoDeIr` en ese
    // caso, ambigüedad 4 de su cabecera). Solo es un error real
    // (`CONDICIONAL_SIN_CONVERGENCIA`) cuando la cadena exterior SÍ espera un
    // punto de continuación concreto (`pararEnExterior !== null`: 'fin' o un
    // hermano siguiente).
    if (convergencia === null && pararEnExterior !== null) {
      this.registrarError({
        tipo: 'CONDICIONAL_SIN_CONVERGENCIA',
        nodoId: nodo.id,
        mensaje:
          `las ramas del condicional '${nodo.id}' no convergen en ningún nodo común ` +
          "(ni siquiera 'fin'); revisa que ambas ramas terminen en el mismo punto de continuación",
      });
      return null;
    }

    // `convergencia` es o bien el post-dominador real, o bien `null` (solo
    // posible aquí cuando `pararEnExterior === null`, ver arriba): en ambos
    // casos es exactamente el `pararEn` correcto para compilar cada rama.
    const sentenciasSi = this.compilarCadena({ modo: 'directo', nodoId: verdaderoDestino }, convergencia);
    if (sentenciasSi === null) return null;
    const sentenciasSino = this.compilarCadena({ modo: 'directo', nodoId: falsoDestino }, convergencia);
    if (sentenciasSino === null) return null;

    const ramaSi = crearSecuencia(sentenciasSi);
    // Coherente con derivarGrafoDeIr (ambigüedad 3 de su cabecera): una rama
    // sin sentencias es indistinguible en el grafo de "rama_sino ausente",
    // así que se normaliza siempre a `null`.
    const ramaSino = sentenciasSino.length === 0 ? null : crearSecuencia(sentenciasSino);

    const sentencia = crearCondicional({
      condicion: /** @type {NodoModelo} */ (nodo.condicion),
      ramaSi,
      ramaSino,
    });
    return {
      sentencia,
      siguiente: convergencia === null ? null : { modo: 'directo', nodoId: convergencia },
    };
  }

  /**
   * @param {NodoModelo} nodo
   * @returns {{ sentencia: NodoModelo, siguiente: Posicion } | null}
   */
  compilarBucle(nodo) {
    const cuerpoArcos = arcosDesde(this.grafo, nodo.id, PUERTO.CUERPO);
    if (cuerpoArcos.length === 0) {
      this.registrarError({
        tipo: 'BUCLE_SIN_CUERPO',
        nodoId: nodo.id,
        mensaje: `falta el arco del cuerpo del bucle '${nodo.id}'`,
      });
      return null;
    }
    if (cuerpoArcos.length > 1) {
      this.registrarError({
        tipo: 'BUCLE_CUERPO_DUPLICADO',
        nodoId: nodo.id,
        mensaje: `el bucle '${nodo.id}' tiene ${cuerpoArcos.length} arcos de cuerpo; debe tener exactamente 1`,
      });
      return null;
    }

    const cuerpoDestino = cuerpoArcos[0].hastaNodoId;
    const sentenciasCuerpo = this.compilarCadena({ modo: 'directo', nodoId: cuerpoDestino }, null);
    if (sentenciasCuerpo === null) return null;
    const cuerpo = crearSecuencia(sentenciasCuerpo);

    /** @type {NodoModelo} */
    let sentencia;
    if (nodo.nodo === TIPO_NODO.BUCLE_MIENTRAS) {
      sentencia = crearBucleMientras({ condicion: /** @type {NodoModelo} */ (nodo.condicion), cuerpo });
    } else if (nodo.nodo === TIPO_NODO.BUCLE_FIN_PARA) {
      sentencia = crearBucleFinPara({
        variable: /** @type {string} */ (nodo.variable),
        inicio: /** @type {NodoModelo} */ (nodo.inicio),
        fin: /** @type {NodoModelo} */ (nodo.fin),
        paso: /** @type {NodoModelo} */ (nodo.paso),
        cuerpo,
      });
    } else {
      sentencia = crearRepetirHasta({ cuerpo, condicion: /** @type {NodoModelo} */ (nodo.condicion) });
    }

    return { sentencia, siguiente: { modo: 'arco', nodoId: nodo.id, puerto: PUERTO.SALIDA } };
  }
}

/**
 * @param {unknown} valor
 */
function requerirNodosArg(valor) {
  if (!Array.isArray(valor)) {
    throw new TypeError('"nodos" debe ser un array de NodoModelo | "inicio" | "fin"');
  }
  return /** @type {NodoOClave[]} */ (valor);
}

/**
 * @param {unknown} valor
 */
function requerirGrafoArg(valor) {
  if (
    typeof valor !== 'object' ||
    valor === null ||
    !Array.isArray(/** @type {Record<string, unknown>} */ (valor).arcos)
  ) {
    throw new TypeError('"grafo" debe ser un grafo de arcos válido ({ arcos: Arco[] })');
  }
  return /** @type {GrafoArcos} */ (valor);
}

/**
 * Compila el grafo de arcos `(nodos, grafo)` a la IR estructurada existente
 * (`Programa` con `cuerpo: Secuencia`), recorriendo desde `'inicio'` (ver
 * cabecera del módulo para el algoritmo completo y DEC-070 para la autoridad
 * estructural). Módulo puro: no toca DOM ni backend.
 *
 * Lanza `TypeError` si `nodos`/`grafo` no tienen, ESTRUCTURALMENTE, la forma
 * esperada (mismo idioma que `validarGrafoArcos`/`derivarGrafoDeIr`). Un
 * grafo bien formado pero que NO resuelve a un programa estructurado válido
 * NUNCA lanza: devuelve `{ programa: null, errores }` con los problemas
 * concretos encontrados.
 * @param {NodoOClave[]} nodos
 * @param {GrafoArcos} grafo
 * @returns {ResultadoCompilacion}
 */
export function compilarGrafoAIr(nodos, grafo) {
  requerirNodosArg(nodos);
  requerirGrafoArg(grafo);

  /** @type {Map<string, NodoOClave>} */
  const nodosPorId = new Map();
  for (const entrada of nodos) {
    nodosPorId.set(idDe(entrada), entrada);
  }

  /** @type {ErrorCompilacion[]} */
  const erroresIniciales = [];
  if (!nodosPorId.has(CLAVE_INICIO)) {
    erroresIniciales.push({
      tipo: 'INICIO_AUSENTE',
      mensaje: "el grafo no contiene el nodo 'inicio'; no hay ningún punto de partida",
    });
  }
  if (!nodosPorId.has(CLAVE_FIN)) {
    erroresIniciales.push({
      tipo: 'FIN_AUSENTE',
      mensaje: "el grafo no contiene el nodo 'fin'; no hay ningún punto de llegada",
    });
  }
  if (erroresIniciales.length > 0) {
    return { programa: null, errores: erroresIniciales };
  }

  const contexto = new ContextoCompilacion(nodosPorId, grafo);
  const sentenciasRaiz = contexto.compilarCadena(
    { modo: 'arco', nodoId: CLAVE_INICIO, puerto: PUERTO.SALIDA },
    CLAVE_FIN
  );

  if (sentenciasRaiz === null) {
    return { programa: null, errores: contexto.errores };
  }

  // Grafo desconectado (ambigüedad 1 de la cabecera: Comentario, invisible al
  // grafo, se excluye deliberadamente de esta comprobación).
  for (const [id, entrada] of nodosPorId) {
    if (id === CLAVE_INICIO || id === CLAVE_FIN) continue;
    if (typeof entrada !== 'string' && entrada.nodo === TIPO_NODO.COMENTARIO) continue;
    if (!contexto.consumidos.has(id)) {
      contexto.registrarError({
        tipo: 'NODO_INALCANZABLE',
        nodoId: id,
        mensaje: `el nodo '${id}' no es alcanzable desde 'inicio'`,
      });
    }
  }
  if (contexto.errores.length > 0) {
    return { programa: null, errores: contexto.errores };
  }

  const programa = crearPrograma({ cuerpo: crearSecuencia(sentenciasRaiz) });
  return { programa, errores: [] };
}
